/**
 * NumberedTimeline.tsx — numbered vertical step list reusing the vts3 visual language.
 *
 * Path B (pfm-43-44, 2026-05-09): parallel component to VideoTutorialSection's
 * VtsTimelineItems. Chosen over Path A (generalising VideoTutorialSection) to
 * keep the Für Elise code path completely untouched — zero regression surface.
 *
 * Visual identity:
 *   - Cream-surface container (inherits from blog body cream).
 *   - Italic gold serif heading at top (Cormorant Garamond italic, --gold-on-cream).
 *   - Vertical gold S-curve SVG thread connecting icons (matches vts3-connector).
 *   - 5 items, each: numeral prefix (1.–5.) · raw philosophy icon (64px desktop /
 *     48px mobile, no disc/circle wrapper) · italic serif title · sans-serif body.
 *   - Icon slot: NO tile, NO border, NO border-radius, NO background — bare on cream,
 *     matching .vts3-item__icon rule exactly.
 *   - Title: Cormorant Garamond italic, --text-dark (matches .vts3-item__title).
 *   - Body: Figtree 400, 16px, --text-dark (matches .vts3-item__body).
 *
 * CSS: .numbered-timeline and .numbered-timeline__* in globals.css.
 *
 * Numbering approach: CSS counter on .numbered-timeline__items (counter-reset)
 * and .numbered-timeline__item (counter-increment). The numeral span uses
 * CSS content: counter(...) so numbering is correct regardless of how children
 * are rendered (TinaMarkdown wrapper, cloneElement injection, etc.).
 *
 * Parent component: <numberedTimeline>
 * Child component:  <numberedTimelineItem>
 *
 * Usage in MDX:
 *   <numberedTimeline heading="5 Steps Towards Masterful Piano Fingering">
 *     <numberedTimelineItem heading="First Version Fingering" icon="performer-interpretation">
 *       Body text here.
 *     </numberedTimelineItem>
 *   </numberedTimeline>
 *
 * NOTE: stepTimeline (disc-circle variant) is a distinct component for use
 * without icons. NumberedTimeline is the icon-first visual treatment.
 * Current consumers: how-to-choose-a-piano-fingering (1 post).
 */

import PhilosophyIcon, { type PhilosophyIconName } from './PhilosophyIcon';

// ---------------------------------------------------------------------------
// NumberedTimelineItem
// ---------------------------------------------------------------------------

interface NumberedTimelineItemProps {
  heading: string;
  icon: PhilosophyIconName;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: any;
}

export function NumberedTimelineItem({
  heading,
  icon,
  children,
}: NumberedTimelineItemProps) {
  return (
    <div className="numbered-timeline__item vts3-item">
      {/* Icon slot — bare on cream, no disc/container. Mirrors .vts3-item__icon. */}
      <div className="numbered-timeline__icon vts3-item__icon" aria-hidden="true">
        <PhilosophyIcon name={icon} width={64} height={64} />
      </div>
      <div className="numbered-timeline__content vts3-item__content">
        {/*
          Numeral prefix via CSS counter (counter-increment on .numbered-timeline__item,
          counter-reset on .numbered-timeline__items). The span uses CSS content:
          counter(step-counter) so each item automatically gets the correct numeral
          regardless of how children arrive (TinaMarkdown wrapper, cloneElement, etc.).
          aria-hidden: true — the heading text already conveys context.
          Visual: italic gold, same font as title.
        */}
        <h3 className="numbered-timeline__heading vts3-item__title">
          <span className="numbered-timeline__numeral" aria-hidden="true" />
          {heading}
        </h3>
        <div className="numbered-timeline__body vts3-item__body">
          {children}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NumberedTimeline (parent)
// ---------------------------------------------------------------------------

interface NumberedTimelineProps {
  heading?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: any;
}

export default function NumberedTimeline({ heading, children }: NumberedTimelineProps) {
  return (
    <section className="numbered-timeline" aria-label={heading}>
      {heading && (
        <h2 className="numbered-timeline__section-heading">{heading}</h2>
      )}
      {/*
        Gold S-curve SVG thread — identical visual treatment to vts3-connector.

        D1 fix (pfm-43-44 continuation 2026-05-09): connector now auto-stretches.
        Previously used `style={{ height: itemCount * 128 }}` which hardcoded 5×128px
        and broke when item body text increased item heights beyond ~128px each.

        Fix: remove the inline height. The CSS class `.vts3-connector` already sets
        `height: 100%` — the inline style was shadowing it. With the inline style
        removed the SVG stretches to match `.vts3-timeline-items` (position:relative
        flex container), which has the natural content height of all items.

        The path uses viewBox="0 0 64 540" with preserveAspectRatio="none" so the
        S-curve meander scales proportionally to whatever height the container reaches.
        The horizontal wobble (control points at x=14 and x=50) is preserved at every
        height — the curve always reads as a hand-drawn S-thread. Only the vertical
        scale changes, which is imperceptible given the gentle meander.

        This is item-count-agnostic: works for 5 items today, 10 items tomorrow.

        Cubic bezier S-meander: spans viewBox from y=32 (top icon center) to y=508
        (bottom icon center, ~32px from bottom of 540 viewBox). Control points create
        the two-pass S shape that matches the Für Elise VTS connector.
        Color: #D4B86E (--gold) — decorative, no WCAG requirement.
        aria-hidden: true — purely decorative.
      */}
      <div className="numbered-timeline__items vts3-timeline-items">
        <svg
          className="numbered-timeline__connector vts3-connector"
          aria-hidden="true"
          viewBox="0 0 64 540"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M 32 32 C 14 160 50 220 32 270 C 14 320 50 380 32 508"
            stroke="#D4B86E"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
        {children}
      </div>
    </section>
  );
}
